import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { Activity, ChevronLeft, Globe, TrendingUp, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

export default function StaffActivitiesPage() {
  const navigate = useNavigate();
  const { fromDate, setFromDate, toDate, setToDate, selectedStaff, setSelectedStaff,
    staffList, timezone, setPreset, formatHours } = useReportFilters();

  const [productivity, setProductivity] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: fromDate, end_date: toDate,
        staff_id: selectedStaff === 'all' ? undefined : selectedStaff
      };
      const [pRes, cRes, eRes] = await Promise.allSettled([
        axios.get(`${API}/admin/activity-reports/productivity`, { params, withCredentials: true }),
        axios.get(`${API}/admin/activity-reports/attendance-compliance`, { params, withCredentials: true }),
        axios.get(`${API}/admin/activity-reports/exceptions`, { params, withCredentials: true }),
      ]);
      if (pRes.status === 'fulfilled') setProductivity(pRes.value.data);
      if (cRes.status === 'fulfilled') setCompliance(cRes.value.data);
      if (eRes.status === 'fulfilled') setExceptions(eRes.value.data);
    } catch (e) { toast.error('Failed to load activities'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [fromDate, toDate, selectedStaff]);

  const prodChartData = (productivity?.records || productivity?.staff_productivity || []).slice(0, 15).map(s => ({
    name: s.staff_name?.split(' ')[0] || s.staff_name,
    score: Math.round(s.efficiency_percent || s.productivity_score || 0),
    hours: +(s.total_work_hours || s.total_hours || 0).toFixed(1),
  }));

  const complianceChartData = (compliance?.records || []).map(r => ({
    name: r.staff_name?.split(' ')[0],
    rate: r.compliance_rate || 0,
  })).slice(0, 12);

  return (
    <div className="space-y-6" data-testid="staff-activities-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Staff Activities</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>Times shown in {timezone}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Preset</Label>
              <div className="flex gap-1">
                {[['today','Today'],['week','This Week'],['month','This Month']].map(([k,l]) => (
                  <Button key={k} size="sm" variant="outline" onClick={() => setPreset(k)} className="text-xs h-8">{l}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">From</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-8 text-sm w-36" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">To</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-8 text-sm w-36" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Staff</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="h-8 text-sm w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading activities...</div>}

      {!loading && (
        <>
          {/* Productivity Section */}
          {productivity && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Productivity Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-blue-600">{((productivity.summary?.overall_efficiency || productivity.summary?.avg_productivity_score) ?? '–').toFixed?.(0) ?? '–'}%</p>
                    <p className="text-xs text-gray-500">Avg Productivity</p>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-indigo-600">{productivity.summary?.total_staff ?? '–'}</p>
                    <p className="text-xs text-gray-500">Total Staff</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-purple-600">{(productivity.summary?.total_active_hours || productivity.summary?.total_hours_worked || 0).toFixed(0)}h</p>
                    <p className="text-xs text-gray-500">Total Hours</p>
                  </div>
                </div>

                {prodChartData.length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={prodChartData} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="score" fill="#3b82f6" name="Productivity %" radius={[3,3,0,0]} />
                      <Bar yAxisId="right" dataKey="hours" fill="#8b5cf6" name="Hours" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {/* Productivity Table */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {['Staff','Dept','Hours Worked','Productivity %','Active Time','Idle Time'].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(productivity.records || productivity.staff_productivity || []).map((s,i)=>(
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{s.staff_name}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{s.department||'–'}</Badge></td>
                          <td className="px-3 py-2 text-blue-600 font-semibold">{formatHours(s.total_work_hours || s.total_hours)}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                <div className="bg-blue-500 h-1.5 rounded-full" style={{width:`${Math.min(100,s.efficiency_percent||s.productivity_score||0)}%`}} />
                              </div>
                              <span className="text-xs font-semibold">{(s.efficiency_percent||s.productivity_score||0).toFixed(0)}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-indigo-600">{formatHours(s.active_hours)}</td>
                          <td className="px-3 py-2 text-amber-600">{formatHours(s.idle_hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance Section */}
          {compliance && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500" />
                  Attendance Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {complianceChartData.length > 0 && (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={complianceChartData} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0,100]} unit="%" />
                      <Tooltip formatter={v => `${v}%`} />
                      <Bar dataKey="rate" fill="#22c55e" name="Compliance Rate" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {['Staff','Expected Days','Present','Late','Absent','Compliance%'].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(compliance.records||[]).map((r,i)=>(
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{r.staff_name}</td>
                          <td className="px-3 py-2">{r.expected_days}</td>
                          <td className="px-3 py-2"><span className="text-indigo-600 font-semibold">{r.present_days}</span></td>
                          <td className="px-3 py-2"><span className="text-amber-600">{r.late_arrivals ?? r.late_days ?? 0}</span></td>
                          <td className="px-3 py-2"><span className="text-red-600">{Math.max(0, r.missed_checkins ?? r.absent_days ?? 0)}</span></td>
                          <td className="px-3 py-2">
                            <span className={`font-bold ${r.compliance_rate>=90?'text-indigo-600':r.compliance_rate>=70?'text-amber-600':'text-red-600'}`}>
                              {r.compliance_rate?.toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exceptions Section */}
          {exceptions && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Exceptions & Alerts
                  {(exceptions.exceptions?.length > 0) && (
                    <Badge className="bg-amber-100 text-amber-700 ml-1">{exceptions.exceptions.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(exceptions.exceptions?.length || 0) === 0 ? (
                  <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl text-sm">
                    No exceptions found for this period.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(exceptions.exceptions || []).slice(0, 30).map((ex, i) => (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                        ex.severity === 'high' ? 'border-red-200 bg-red-50' :
                        ex.severity === 'medium' ? 'border-amber-200 bg-amber-50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                          ex.severity === 'high' ? 'text-red-500' :
                          ex.severity === 'medium' ? 'text-amber-500' : 'text-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{ex.staff_name}</p>
                          <p className="text-xs text-gray-600 mt-0.5">{ex.description || ex.detail || ex.exception_type?.replace(/_/g,' ')}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-xs ${
                            ex.severity === 'high' ? 'bg-red-100 text-red-700' :
                            ex.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{ex.severity}</Badge>
                          <p className="text-xs text-gray-400 mt-1">{ex.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
      </>
    )}
  </div>
  );
}
